import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";

import { clip } from "fond/draw/helper";
import { feature } from "fond/turf";

import CustomSnapPolygonMode from "./snap/CustomSnapPolygonMode";
import { removeGuides } from "./helper";

const {
  constants: Constants,
  lib: { doubleClickZoom },
} = MapboxDraw;

const drawPolygon: DrawCustomMode = {
  ...CustomSnapPolygonMode,

  onSetup: function onSetup(opts) {
    // We store the mode options so that we can access them later to reinitialize the mode if needed (e.g. after undo/redo)
    this._ctx.options.modeOptions = opts || {};

    const state = CustomSnapPolygonMode.onSetup?.call(this, opts);
    state.options = this._ctx.options;
    removeGuides.call(this);

    return state;
  },

  /**
   * We customize the onStop method to clip the polygon to other polygons if
   * autoClip is enabled. We always call the parent CustomSnapPolygonMode.onStop to
   * ensure proper cleanup of snap-mode event listeners.
   * @param state The current state of the mode.
   * @returns void
   */
  onStop(state) {
    if (state.options?.autoClip) {
      this.updateUIClasses({ mouse: Constants.cursors.NONE });
      doubleClickZoom.enable(this);
      this.activateUIButton();

      // check to see if we've deleted this feature
      if (this.getFeature(state.polygon.id) === undefined) {
        // Still need to call parent cleanup even if feature was deleted
        CustomSnapPolygonMode.onStop?.call(this, state);
        return;
      }

      // remove last added coordinate
      state.polygon.removeCoordinate(`0.${state.currentVertexPosition}`);
      // clip the polygon to other polygons
      // this will remove any overlapping coordinates
      const newCoords = clip(
        feature(state.polygon),
        this._ctx.api.getAll().features.filter(({ id }) => id !== state.polygon.id)
      );
      // update the polygon with the clipped coordinates
      state.polygon.setCoordinates(newCoords.geometry.coordinates);

      if (state.polygon.isValid()) {
        // Here we need to clip the polygon to other polygons
        this.map.fire(Constants.events.CREATE, {
          features: [state.polygon.toGeoJSON()],
        });
      } else {
        this.deleteFeature(state.polygon.id, { silent: true });
        this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
      }
    }

    // Always call parent onStop to clean up snap-mode event listeners (moveend, etc.)
    CustomSnapPolygonMode.onStop?.call(this, state);

    // Clear stored options when exiting the mode
    this._ctx.options.modeOptions = {};
  },
};

export default drawPolygon;
